import type { RenderSmartCardOptions } from "@/components/heads-up/cards";
import type { HeadsUpViewState, HeadsUpClickHandlers } from "@/components/heads-up/context";
import type { SmartCardRow } from "@/domains/tanstack-db";
import type { Maybe } from "@/domains/common/types";
import type { INoteObservable } from "@/store/note/types";

export enum HeadsUpActivityState {
  Idle = "IDLE",
  Hydrating = "HYDRATING",
  Generating = "GENERATING",
  Refreshing = "REFRESHING",
  Regenerating = "REGENERATING",
  FindingMore = "FINDING_MORE",
}

export interface HeadsUpContextSlice {
  contextId: string | null;
  activeNoteId: string | null;
  activeNoteTitle: string | null;
  isDatabaseReady: boolean;
  spaceAccountId: string | null;
  smartViewId: string | null;
  untrashedNoteCount: number | null;
}

export interface HeadsUpContentVisibility {
  get: (cardId: string) => number | undefined;
  set: (cardId: string, updater: (current: number | undefined) => number) => void;
}

export interface HeadsUpPendingSnapshot {
  accept: () => void;
}

export interface HeadsUpContentSlice {
  cards: SmartCardRow[];
  primaryCards: SmartCardRow[];
  findMoreCards: SmartCardRow[];
  viewState: HeadsUpViewState;
  hasView: boolean;
  refreshedAt: string | null;
  formattedRelativeTime: string | null;
  pendingSnapshot: HeadsUpPendingSnapshot | null;
  error: string | null;
  isLoading: boolean;
  visibility: HeadsUpContentVisibility;
}

export interface HeadsUpActivitySlice {
  state: HeadsUpActivityState;
  isBusy: boolean;
  isGenerating: boolean;
  isGeneratingBrief: boolean;
  isRefreshing: boolean;
  isRegenerating: boolean;
  isFindingMore: boolean;
  hasFoundMore: boolean;
  isLiveActive: boolean;
  canGenerateBrief: boolean;
  canRefresh: boolean;
  canRegenerate: boolean;
  canFindMore: boolean;
}

export interface HeadsUpInteractionSlice {
  generateMeetingBrief: () => void;
  refresh: () => void;
  regenerate: () => void;
  findMore: () => void;
  openHeadsUp: () => void;
  clickHandlers: HeadsUpClickHandlers;
}

export interface HeadsUpResourceSlice {
  loadNote: (noteId: string) => Promise<Maybe<INoteObservable>>;
  isNoteLoading: (noteId: string) => boolean;
  cardRenderOptions: RenderSmartCardOptions;
}

export interface HeadsUpUiSlice {
  isHovered: boolean;
  setHovered: (value: boolean) => void;
  isChatOverlayOpen: boolean;
}

export interface HeadsUpSidebarModel {
  context: HeadsUpContextSlice;
  content: HeadsUpContentSlice;
  activity: HeadsUpActivitySlice;
  interactions: HeadsUpInteractionSlice;
  resources: HeadsUpResourceSlice;
  ui: HeadsUpUiSlice;
}
